/**
 * How many digits of a figure a person actually reads.
 *
 * The API sends exact decimals as strings, and exactness is a property of the
 * transport: it is what stops a rate from drifting between the server and the
 * screen. On the screen it is noise. `0.936507936507936507936507936507` is not
 * a more truthful recovery rate than `93.7 %`, it is the same rate with
 * twenty-four digits nobody can act on -- and it is wide enough to push the row
 * it sits in past the edge of its own card.
 *
 * Money already had this rule and its own place to keep it. Everything that is
 * not money had neither, so each surface decided for itself, and deciding for
 * yourself means printing what arrived. This module is that missing place: one
 * function per kind of figure, so a component asks for "a rate" rather than
 * choosing a precision.
 *
 * Nothing here parses a value into a number. The rule from `money.ts` holds for
 * every figure, not only for money: JavaScript has one numeric type and it is a
 * float, so a conversion loses the very exactness the string was carrying.
 * Moving a decimal point is string surgery.
 */
import { grouped, toPlaces } from "@/shared/money/money";

/** A figure that came from the API and must not be computed with. */
export type Metric = string;

/** Narrow no-break space: keeps the unit with its figure, without crowding it. */
const THIN = "\u202f";

/** Shown when the server had nothing to send. Never a zero: they differ. */
export const ABSENT = "—";

/**
 * The same value with the decimal point moved right, by string surgery.
 *
 * Used to read a ratio as a percentage. `0.0005` and `0.05 %` are one figure in
 * two units, and turning the first into the second by multiplying would be the
 * one arithmetic operation this module exists to avoid.
 */
function shifted(value: Metric, places: number): string {
  const negative = value.startsWith("-");
  const body = negative || value.startsWith("+") ? value.slice(1) : value;
  const point = body.indexOf(".");
  const whole = point === -1 ? body : body.slice(0, point);
  const fraction = point === -1 ? "" : body.slice(point + 1);

  const digits = whole + fraction.padEnd(places, "0");
  const cut = whole.length + places;
  const head = digits.slice(0, cut).replace(/^0+(?=\d)/, "");
  const tail = digits.slice(cut);
  return (negative ? "-" : "") + head + (tail === "" ? "" : "." + tail);
}

/**
 * A rate between zero and one, read as a percentage.
 *
 * One decimal by default. A recovery rate of 93.7 % and one of 93.65 % lead to
 * the same decision, and the second digit is bought with width the row does not
 * have.
 */
export function percent(value: Metric | null, places = 1): string {
  if (value === null) return ABSENT;
  return toPlaces(shifted(value, 2), places) + THIN + "%";
}

/**
 * A bounded score that is not a percentage -- a weighting, a saturation.
 *
 * Four decimals: enough to separate two candidates that are genuinely close,
 * short enough to sit in a column. Shown as it is measured, not as a
 * percentage, because it has no denominator a person could name.
 */
export function ratio(value: Metric | null, places = 4): string {
  if (value === null) return ABSENT;
  return toPlaces(value, places);
}

/**
 * A quantity large enough to need its digits grouped -- a turnover, a volume.
 *
 * No decimals: a daily turnover is read in millions, and the cents of it are
 * three digits of noise on a line that already has to fit a symbol and a score.
 */
export function amount(value: Metric | null): string {
  if (value === null) return ABSENT;
  return grouped(toPlaces(value, 0));
}

/** A whole count. Never rounded, never abbreviated: it is already exact. */
export function count(value: number | null): string {
  return value === null ? ABSENT : String(value);
}
